import type { SomeButtonModel } from '@companion-app/shared/Model/ButtonModel.js'
import type { ControlLocation } from '@companion-app/shared/Model/Common.js'
import { CDropdown, CButton, CDropdownToggle, CDropdownMenu, CDropdownItem } from '@coreui/react'
import { useCallback } from 'react'
import type { GenericConfirmModalRef } from '~/Components/GenericConfirmModal.js'
import { trpc, useMutationExt } from '~/Resources/TRPC'

export function SelectButtonTypeDropdown({
	location,
	resetModalRef,
	configRef,
}: {
	location: ControlLocation
	resetModalRef: React.MutableRefObject<GenericConfirmModalRef | null>
	configRef: React.MutableRefObject<SomeButtonModel | undefined> | undefined
}): React.JSX.Element {
	const resetControlMutation = useMutationExt(trpc.controls.resetControl.mutationOptions())

	const setButtonType = useCallback(
		(newType: string) => {
			let show_warning = false

			const currentType = configRef?.current?.type
			if (currentType === newType) {
				// No point changing style to itself
				return
			}

			if (currentType && currentType !== 'pageup' && currentType !== 'pagedown' && currentType !== 'pagenum') {
				if (newType === 'pageup' || newType === 'pagedown' || newType === 'pagenum') {
					show_warning = true
				}
			}

			const doChange = () => {
				resetControlMutation.mutateAsync({ location, newType }).catch((e) => {
					console.error(`Set type failed: ${e}`)
				})
			}

			if (show_warning) {
				resetModalRef.current?.show(
					`Change style`,
					`Changing to this button style will erase actions and feedbacks configured for this button - continue?`,
					'OK',
					() => {
						doChange()
					}
				)
			} else {
				doChange()
			}
		},
		[resetControlMutation, location, configRef, resetModalRef]
	)

	return (
		<CDropdown variant="btn-group">
			<CButton color="danger" onClick={() => setButtonType('button')} title="Create regular button.">
				Create button
			</CButton>
			<CDropdownToggle
				color="danger"
				split
				style={{ opacity: 0.7, padding: '0 0.75em' }}
				aria-label="Toggle Button-Type Dropdown"
				title="Toggle Button-Type Dropdown"
			/>
			<CDropdownMenu>
				<CDropdownItem onClick={() => setButtonType('button')}>Regular button</CDropdownItem>
				<CDropdownItem onClick={() => setButtonType('pageup')}>Page up</CDropdownItem>
				<CDropdownItem onClick={() => setButtonType('pagenum')}>Page number</CDropdownItem>
				<CDropdownItem onClick={() => setButtonType('pagedown')}>Page down</CDropdownItem>
			</CDropdownMenu>
		</CDropdown>
	)
}
